import { DateTime, Settings } from "luxon";
import type {Query, SortItem} from "@/models.ts";

// Set default locale
Settings.defaultLocale = "it";

// Type definitions
type FilterOptions = {
    query: any;
    limit: number;
    page: number;
    sort: string | null;
    order: string | null;
};

// Helper functions
const isNumber = (a: any): a is number => typeof a === 'number';
const isString = (a: any): a is string => typeof a === 'string';
const isNil = (a: any): boolean => a === null || a === undefined;

const toDateTime = (a?: any): DateTime => {
    if (!a) return DateTime.now();
    if (a instanceof DateTime) return a;
    if (a instanceof Date && !isNaN(a.getTime())) return DateTime.fromJSDate(a);
    if (isString(a)) return DateTime.fromISO(a);
    return DateTime.now();
};

const getChildrenOf = (ctg: any, map: Record<string, any[]>): any => {
    if (map[ctg._id]) {
        let children = map[ctg._id];
        for (let i = 0; i < children.length; i++) {
            let item = children[i];
            children[i] = getChildrenOf(item, map);
        }
        ctg.children = children;
    }
    return ctg;
};

const index = {
    isString,
    isNumber,
    isNil,
    toDateTime,
    dateOverlaps(startA: any, endA: any, startB: any, endB: any): boolean {
        return !(endA <= startB || startA >= endB);
    },
    noFilter(): FilterOptions {
        return { query: {}, limit: 0, page: 0, sort: null, order: null };
    },
    pageFilter(query: any, limit?: number, page?: number): FilterOptions {
        if (!limit) limit = 0;
        if (!page) page = 1;
        return { query, limit, page, sort: null, order: null };
    },
    fullFilter(query: any, limit: number, page: number, sort: string | null, order: string | null): FilterOptions {
        return { query, limit, page, sort, order };
    },
    calcAge(birth: any): number {
        return -Math.floor(toDateTime(birth).diffNow('years').years);
    },
    gradient: "data:image/png...(5562 characters truncated, it's impossible to view or edit this line)",
    isValidPhoneNumber: (num: string): boolean => {
        let re = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g;
        return re.test(num);
    },
    isValidEmail: (mail: string): boolean => {
        let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(mail).toLowerCase());
    },
    isValidColor: (color: string): boolean => {
        let re = /^#[0-9A-F]{6}$/i;
        return re.test(color);
    },
    isValidCF: (value: string): boolean => {
        let re = /^(?:[A-Z][AEIOU][AEIOUX]|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/i;
        return re.test(value);
    },
    copy: <T>(obj: T): T => {
        return JSON.parse(JSON.stringify(obj));
    },
    stringSearch(value: string, test: string): boolean {
        return value.toLowerCase().indexOf(test.toLowerCase()) !== -1;
    },
    getMouseCoordinates(e: MouseEvent, target: HTMLElement): { x: number, y: number } {
        const rect = target.getBoundingClientRect();
        const x = e.clientX - Math.round(rect.left); // x position within the element.
        const y = e.clientY - Math.round(rect.top);  // y position within the element.
        return { x, y };
    },
    alphabeticalSort(array: any[], key?: string, ascending?: boolean): void {
        console.log(array);
        array.sort(function(a, b) {
            let nameA = key ? a[key].toLowerCase() : a.toLowerCase();
            let nameB = key ? b[key].toLowerCase() : b.toLowerCase();
            if (ascending) {
                if (nameA < nameB) // sort string ascending
                    return -1;
                if (nameA > nameB)
                    return 1;
            } else {
                if (nameA < nameB) // sort string ascending
                    return 1;
                if (nameA > nameB)
                    return -1;
            }
            return 0; // default return value (no sorting)
        });
    },
    toFloat(val: any, defaultVal: number): number {
        if (val === null || val === undefined) return defaultVal;
        if (isNumber(val)) return val;
        if (isString(val)) {
            let num = parseFloat(val.replace(",", "."));
            if (!Number.isNaN(num)) {
                return num;
            }
        }
        return defaultVal;
    },
    toInt(val: any, defaultVal: number): number {
        if (isNil(val)) return defaultVal;
        if (isNumber(val)) return val;
        if (isString(val)) {
            let num = parseInt(val.replace(",", "."), 10);
            if (!Number.isNaN(num)) {
                return num;
            }
        }
        return defaultVal;
    },
    formatTime(date: any): string {
        return toDateTime(date).toFormat("HH:mm");
    },
    formatDate(date: any): string {
        return toDateTime(date).toFormat("dd/MM/yyyy");
    },
    formatDateTime(date: any): string {
        return toDateTime(date).toFormat("dd/MM/yyyy, HH:mm");
    },
    formatDateExtended(date:any):string {
        return toDateTime(date).toFormat("EEEE dd/MM/yyyy");
    },
    getObjectInArray<T>(items: T[], byKey: keyof T, value: any): T | null {
        let filter = items.filter(item => item[byKey] === value);
        return filter.length ? filter[0] : null;
    },
    equalsCheck(a: any, b: any): boolean {
        // If they point to the same instance of the array
        if (a === b)
            return true;

        // If they point to the same instance of date
        if (a instanceof Date && b instanceof Date)
            return a.getTime() === b.getTime();

        // If both of them are not null and their type is not an object
        if (!a || !b || (typeof a !== 'object' && typeof b !== 'object'))
            return a === b;

        // This means the elements are objects
        // If they are not the same type of objects
        if (a.prototype !== b.prototype)
            return false;

        // Check if both of the objects have the same number of keys
        const keys = Object.keys(a);
        if (keys.length !== Object.keys(b).length)
            return false;

        // Check recursively for every key in both
        return keys.every(k => index.equalsCheck(a[k], b[k]));
    },
    checkImageSize(file: File, width: number, height: number, canBeLess?: boolean): Promise<void> {
        if (canBeLess === undefined) canBeLess = false;
        return new Promise((resolve, reject) => {
            var fr = new FileReader();
            fr.onload = function() { // file is loaded
                var img = new Image();
                img.onload = () => { // image is loaded; sizes are available
                    if (canBeLess) {
                        if (img.width > width) {
                            reject(`La larghezza dell'immagine deve essere ${width}px ed invece è di ${img.width}px`);
                            return;
                        }

                        if (img.height > height) {
                            reject(`La larghezza dell'immagine deve essere ${height}px ed invece è di ${img.height}px`);
                            return;
                        }
                    } else {
                        if (img.width !== width) {
                            reject(`La larghezza dell'immagine deve essere ${width}px ed invece è di ${img.width}px`);
                            return;
                        }

                        if (img.height !== height) {
                            reject(`La larghezza dell'immagine deve essere ${height}px ed invece è di ${img.height}px`);
                            return;
                        }
                    }

                    resolve();
                };
                img.onerror = () => {
                    reject(`Impossibile leggere le informazioni dell'immagine`);
                };
                img.src = fr.result as string; // is the data URL because called with readAsDataURL
            };
            fr.readAsDataURL(file);
        });
    },
    genPaymentUID(): string {
        return `MANUAL${DateTime.now().toUnixInteger()}`;
    },
    getSessionQuery(name:string):Query {
        const q = sessionStorage.getItem(`${name}Query`)
        if(!q) return {};
        try {
            return JSON.parse(q) as Query
        } catch (e) {
            return {}
        }
    },
    setSessionQuery(name:string, value:Query):void {
        sessionStorage.setItem(`${name}Query`, JSON.stringify(value));
    },
    sortToString(sort?:SortItem[]):string|undefined {
        if(!sort || !sort.length) return undefined;
        return sort.map(s => {
            return `${s.order === 'asc' || s.order === false ? '+':'-'}${s.key}`
        }).join(',')
    }
};

export default index;